/**
 * usePushDeepLinkRead — NP-1 mark-read-on-click bridge.
 *
 * A push deep-link arrives as `<portal>/<screen>?pn=<notificationId>&pnt=<type>` (the SW
 * can't hold a Bearer token, so the server threads the id through the URL; `pnt` is the
 * [REQ-AUG-30 M.6 / USAGE-1] tap-metric type — a push opened from the device is a
 * notification tap exactly like an in-app one, and this URL is the only place its type
 * survives to). On navigation, mark that notification read, invalidate the shared unread
 * count, and strip both params so a refresh/back doesn't re-fire.
 *
 * Skew note: an old server sends no `pnt` → the tap simply isn't counted (analytics degrades
 * silently, never breaks); `pnt` is validated by the server registry, so a garbled value is a
 * dropped counter, not junk data.
 *
 * @module hooks/usePushDeepLinkRead
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { UNREAD_COUNT_QUERY_KEY } from './useUnreadNotificationCount';
import { trackUsage } from '../utils/usage-core'; // core: counters only, no transport behind it

/** Arriving from a push (…?pn=<id>) → mark that notification read + strip the params. */
export function usePushDeepLinkRead(): void {
  const location = useLocation();
  const queryClient = useQueryClient();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pn = params.get('pn');
    if (!pn) return;
    const pnt = params.get('pnt');
    if (pnt) trackUsage(`notif.${pnt}`, 'notif_tap'); // [USAGE-1] push-opened tap
    api.patch(`/notifications/${pn}/read`)
      .then(() => queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_QUERY_KEY }))
      .catch(() => {})
      .finally(() => {
        params.delete('pn');
        params.delete('pnt');
        const qs = params.toString();
        window.history.replaceState({}, '', location.pathname + (qs ? `?${qs}` : ''));
      });
  }, [location.search, location.pathname, queryClient]);
}
